import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { AlertOctagon, Plus, X, Trash2, ChevronDown } from "lucide-react";
import { useAuth } from "../lib/auth";
import DestCityFilter from "../components/DestCityFilter";

const PROJECT_CATEGORIES = ["Cleanliness","Meals","Rules","Curfew","Other Participants","Team members","Other"];
const COUNTRIES = ["India", "Nepal", "Sri Lanka", "South Africa", "Uganda", "Nigeria", "Kenya", "Bolivia", "Thailand", "Vietnam"];

// Status workflow — pill styling matches the Sales/Bookings palette so the
// three lifecycle states are instantly recognisable from across the table.
const STATUS_OPTIONS = [
  { value: "open",        label: "Open",         cls: "bg-red-100 text-red-800 border-red-200" },
  { value: "in_progress", label: "In-progress",  cls: "bg-amber-100 text-amber-800 border-amber-200" },
  { value: "resolved",    label: "Resolved",     cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
];
const statusMeta = (s) => STATUS_OPTIONS.find(o => o.value === s) || STATUS_OPTIONS[0];

export default function Issues() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [partners, setPartners] = useState([]);
  const [participants, setParticipants] = useState([]); // { customer, partner_name, destination, city }
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    kind: user.effective_role === "GEHM" ? "GEHM_HOUSE" : "PCWM_PROJECT",
    participant_name: "",
    partner_name: "",
    destination: user.destination || "",
    city: user.city || "",
    date: new Date().toISOString().slice(0,10),
    category: "",
    description: "",
    action_plan: "",
  });
  const [filter, setFilter] = useState({ destination: "", city: "" });
  const [statusFilter, setStatusFilter] = useState("all"); // all | open | in_progress | resolved
  const canManage = ["FOUNDER", "BDOM"].includes(user.effective_role);

  const load = async () => {
    const q = new URLSearchParams();
    if (filter.destination) q.set("destination", filter.destination);
    if (filter.city) q.set("city", filter.city);
    const [i, p, pt, bk] = await Promise.all([
      api.get(`/issues?${q.toString()}`),
      api.get("/projects"),
      api.get("/partners").catch(() => ({ data: [] })),
      api.get("/program-bookings").catch(() => ({ data: [] })),
    ]);
    setRows(i.data); setProjects(p.data); setPartners(pt.data || []);
    // Build a de-duped active participant registry keyed by customer name.
    // Latest booking wins so we always carry the freshest partner + location
    // attribution into the picker; that's what the reporter cares about.
    const byName = new Map();
    for (const b of (bk.data || [])) {
      const nm = (b.customer || "").trim();
      if (!nm) continue;
      byName.set(nm.toLowerCase(), {
        name: nm,
        partner_name: b.partner_name || "",
        destination: b.destination || "",
        city: b.city || b.project_placement || "",
      });
    }
    setParticipants([...byName.values()].sort((a, b) => a.name.localeCompare(b.name)));
  };
  useEffect(() => { load(); }, [filter.destination, filter.city]);

  const filteredRows = useMemo(() => {
    if (statusFilter === "all") return rows;
    return rows.filter(r => (r.status || "open") === statusFilter);
  }, [rows, statusFilter]);

  const statusCounts = useMemo(() => {
    const c = { open: 0, in_progress: 0, resolved: 0 };
    for (const r of rows) c[r.status || "open"] = (c[r.status || "open"] || 0) + 1;
    return c;
  }, [rows]);

  // Called when the reporter picks a participant from the datalist. If the
  // typed value matches a known participant (case-insensitive), auto-fill
  // partner + destination + city so scoping is right by default.
  const onParticipantChange = (val) => {
    const match = participants.find(p => p.name.toLowerCase() === val.trim().toLowerCase());
    setForm(v => ({
      ...v,
      participant_name: val,
      partner_name: match?.partner_name || v.partner_name,
      destination: match?.destination || v.destination,
      city: match?.city || v.city,
    }));
  };

  const cycleStatus = async (issue) => {
    const cur = issue.status || "open";
    const nextIdx = (STATUS_OPTIONS.findIndex(o => o.value === cur) + 1) % STATUS_OPTIONS.length;
    const next = STATUS_OPTIONS[nextIdx].value;
    try {
      await api.patch(`/issues/${issue.id}/status`, { status: next });
      toast.success(`Marked ${STATUS_OPTIONS[nextIdx].label.toLowerCase()}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update status"); }
  };

  const isHouse = form.kind === "GEHM_HOUSE";
  const categoryOptions = isHouse ? PROJECT_CATEGORIES : projects.map(p=>p.name);

  const submit = async () => {
    if (!form.participant_name || !form.category) return toast.error("Participant & category required");
    if (!form.destination || !form.city) return toast.error("Destination + City required so only the concerned staff (and Founder) get pinged");
    await api.post("/issues", form);
    setForm(v => ({ ...v, participant_name:"", partner_name:"", category:"", description:"", action_plan:"" }));
    setOpen(false); await load();
    toast.error("Issue broadcast to concerned team & Founder");
  };
  const del = async (id) => { await api.delete(`/issues/${id}`); await load(); };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)] flex items-center gap-1.5"><AlertOctagon size={12}/> Issue Reporting</div>
          <h1 className="text-2xl font-semibold">{user.effective_role === "GEHM" ? "House issues" : user.effective_role === "PCWM" ? "Project issues" : "All issues"}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DestCityFilter value={filter} onChange={setFilter} />
          <button onClick={()=>setOpen(true)} data-testid="issue-new" className="text-sm px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-semibold flex items-center gap-1.5"><Plus size={14}/> Report issue</button>
        </div>
      </div>

      {open && (
        <div className="aii-card p-6 space-y-4 border-l-4 border-red-500">
          <div className="flex justify-between"><h3 className="font-semibold">Report an issue</h3><button onClick={()=>setOpen(false)}><X size={16}/></button></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Kind</label>
              <select value={form.kind} onChange={e=>setForm(v=>({...v,kind:e.target.value,category:""}))} className="input-aii" data-testid="issue-kind">
                <option value="PCWM_PROJECT">Project (PCWM)</option>
                <option value="GEHM_HOUSE">House (GEHM)</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Participant name</label>
              <input
                list="issue-participants-list"
                value={form.participant_name}
                onChange={e => onParticipantChange(e.target.value)}
                className="input-aii"
                data-testid="issue-participant"
                placeholder={participants.length ? "Type or pick a participant…" : "Full name"}
                autoComplete="off"
              />
              <datalist id="issue-participants-list">
                {participants.map(p => (
                  <option key={p.name.toLowerCase()} value={p.name}>
                    {[p.partner_name, p.destination, p.city].filter(Boolean).join(" · ")}
                  </option>
                ))}
              </datalist>
              {participants.length > 0 && (
                <div className="text-[10px] text-[color:var(--aii-muted)] mt-0.5">
                  Picking a name auto-fills Partner · Destination · City · below.
                </div>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Partner (sending org)</label>
              <input list="issue-partners-list" value={form.partner_name} onChange={e=>setForm(v=>({...v,partner_name:e.target.value}))} className="input-aii" data-testid="issue-partner" placeholder="Global Volunteer Univ"/>
              <datalist id="issue-partners-list">
                {partners.map(p => <option key={p.id || p.name} value={p.company_name || p.name}/>)}
              </datalist>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Destination</label>
              <select value={form.destination} onChange={e=>setForm(v=>({...v,destination:e.target.value}))} className="input-aii" data-testid="issue-destination">
                <option value="">— pick —</option>{COUNTRIES.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">City</label>
              <input value={form.city} onChange={e=>setForm(v=>({...v,city:e.target.value}))} className="input-aii" data-testid="issue-city" placeholder="Jaipur, Kandy, …"/>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Date</label>
              <input type="date" value={form.date} onChange={e=>setForm(v=>({...v,date:e.target.value}))} className="input-aii"/>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">{isHouse ? "Category" : "Project"}</label>
              <select value={form.category} onChange={e=>setForm(v=>({...v,category:e.target.value}))} className="input-aii" data-testid="issue-category">
                <option value="">Select…</option>{categoryOptions.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div><label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Explanation</label><textarea rows={3} value={form.description} onChange={e=>setForm(v=>({...v,description:e.target.value}))} className="input-aii" data-testid="issue-description"/></div>
          <div><label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Action plan</label><textarea rows={2} value={form.action_plan} onChange={e=>setForm(v=>({...v,action_plan:e.target.value}))} className="input-aii" data-testid="issue-action"/></div>
          <p className="text-[11px] text-[color:var(--aii-muted)] leading-relaxed">
            Only staff assigned to <b>{form.destination || "…"} · {form.city || "…"}</b> and the Founder will see this issue in their list. If you change destination/city, visibility shifts accordingly.
          </p>
          <div className="flex justify-end"><button onClick={submit} data-testid="issue-submit" className="bg-red-600 text-white font-semibold px-5 py-2 rounded-md hover:bg-red-700">Report — broadcast emergency</button></div>
        </div>
      )}

      {/* Status filter chips + counts */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="issue-status-chips">
        {[
          { value: "all",         label: "All",         count: rows.length,               cls: "bg-slate-100 text-slate-700 border-slate-200" },
          { value: "open",        label: "Open",        count: statusCounts.open,         cls: "bg-red-100 text-red-800 border-red-200" },
          { value: "in_progress", label: "In-progress", count: statusCounts.in_progress,  cls: "bg-amber-100 text-amber-800 border-amber-200" },
          { value: "resolved",    label: "Resolved",    count: statusCounts.resolved,     cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
        ].map(chip => (
          <button
            key={chip.value}
            type="button"
            onClick={() => setStatusFilter(chip.value)}
            data-testid={`issue-status-chip-${chip.value}`}
            className={`text-xs px-3 py-1.5 border rounded-full font-semibold flex items-center gap-1.5 transition ${statusFilter === chip.value ? `${chip.cls} ring-2 ring-offset-1 ring-brand/40` : "bg-white text-[color:var(--aii-text-2)] border-[color:var(--aii-border)] hover:bg-slate-50"}`}
          >
            {chip.label}
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/70 border border-black/5 font-bold">{chip.count}</span>
          </button>
        ))}
      </div>

      <div className="aii-card overflow-x-auto">
        <div className="overflow-x-auto">
          <table className="aii-table">
            <thead><tr><th>Status</th><th>Date</th><th>Kind</th><th>Participant</th><th>Partner</th><th>Destination</th><th>City</th><th>Category</th><th>Description</th><th>Action plan</th><th>Reporter</th><th></th></tr></thead>
            <tbody>
              {filteredRows.length===0 && <tr><td colSpan={12} className="text-center py-10 text-[color:var(--aii-muted)]">{rows.length === 0 ? "No issues reported." : `No ${statusFilter.replace("_", "-")} issues.`}</td></tr>}
              {filteredRows.map(i=>{
                const meta = statusMeta(i.status || "open");
                return (
                  <tr key={i.id} data-testid={`issue-row-${i.id}`}>
                    <td>
                      {canManage ? (
                        <button
                          type="button"
                          onClick={() => cycleStatus(i)}
                          data-testid={`issue-status-${i.id}`}
                          title="Click to cycle: Open → In-progress → Resolved"
                          className={`text-[10px] px-2 py-1 rounded-full font-bold uppercase border inline-flex items-center gap-1 hover:opacity-80 ${meta.cls}`}
                        >
                          {meta.label} <ChevronDown size={10}/>
                        </button>
                      ) : (
                        <span data-testid={`issue-status-${i.id}`}
                              className={`text-[10px] px-2 py-1 rounded-full font-bold uppercase border ${meta.cls}`}>
                          {meta.label}
                        </span>
                      )}
                    </td>
                    <td>{i.date}</td>
                    <td><span className={`pill ${i.kind==='GEHM_HOUSE'?'pill-amber':'pill-gray'}`}>{i.kind==='GEHM_HOUSE'?'House':'Project'}</span></td>
                    <td className="font-medium">{i.participant_name || i.client_name || "—"}</td>
                    <td className="text-xs">{i.partner_name || <span className="text-[color:var(--aii-muted)]">—</span>}</td>
                    <td className="text-xs">{i.destination || <span className="text-[color:var(--aii-muted)]">—</span>}</td>
                    <td className="text-xs">{i.city || <span className="text-[color:var(--aii-muted)]">—</span>}</td>
                    <td>{i.category}</td>
                    <td className="max-w-xs whitespace-pre-wrap">{i.description}</td>
                    <td className="max-w-xs whitespace-pre-wrap">{i.action_plan}</td>
                    <td className="text-xs text-[color:var(--aii-muted)]">{i.reported_by_name} ({i.reported_by_role})</td>
                    <td className="text-right">
                      {canManage && <button onClick={()=>del(i.id)} data-testid={`issue-del-${i.id}`} className="text-[color:var(--aii-muted)] hover:text-red-600"><Trash2 size={14}/></button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
